/**
 * Generate monthly data points for charts and analysis.
 */

import type { ConfigId, MonthlyDataPoint, PlanRates, UserConfig } from '@/types'
import { DAYS_IN_MONTH } from '@/data/constants'
import { LOCATION_DATA } from '@/data/locationData'
import { MONTHS } from '@/data/constants'
import { getSeasonalFactors } from './seasonalFactors'
import { calculateMonthlyMetrics } from './calculateMonthlyMetrics'
import { BATTERY_USABLE_FRAC, BATTERY_ROUND_TRIP } from '@/data/constants'

export const generateMonthlyData = (
  planRates: PlanRates,
  configId: ConfigId,
  userConfig: UserConfig,
  simulationMonthlyData: number[]
): MonthlyDataPoint[] => {
  const locData = LOCATION_DATA[userConfig.location]
  const locMult = userConfig.location === 'Custom' ? (userConfig.customLocationMultiplier ?? 1.0) : (locData ? locData.multiplier : 1.0)
  const locPriceMult = locData ? locData.priceMult : 1.0

  return MONTHS.map((month, index) => {
    const monthAdjust =
      simulationMonthlyData.length > 0 ? (simulationMonthlyData[index % 12] ?? 0) : 0
    const metrics = calculateMonthlyMetrics(
      index,
      userConfig,
      planRates,
      configId,
      locMult,
      locPriceMult,
      monthAdjust
    )

    // SoC derived from actual daily stored vs usable capacity
    const locData2 = LOCATION_DATA[userConfig.location]
    const locMult2 = userConfig.location === 'Custom' ? (userConfig.customLocationMultiplier ?? 1.0) : (locData2 ? locData2.multiplier : 1.0)
    const { solarFactor: sf } = getSeasonalFactors(index)
    const psh2 = 4.0 * sf
    const simMult2 =
      1 +
      (simulationMonthlyData.length > 0 ? (simulationMonthlyData[index % 12] ?? 0) : 0) / 100
    const dailyGen2 =
      (configId !== 'raw' ? userConfig.pvSize : 0) *
      psh2 *
      (userConfig.pvGeneration / 100) *
      (1 - userConfig.systemLosses / 100) *
      locMult2 *
      simMult2

    const isBatActive = userConfig.useEss
    const dailySelfUse2 =
      metrics.solarGenerationKwh > 0
        ? (metrics.solarGenerationKwh / DAYS_IN_MONTH) * (isBatActive ? 0.55 : 0.35)
        : 0
    const dailySurplus2 = Math.max(0, dailyGen2 - dailySelfUse2 / BATTERY_USABLE_FRAC)
    const dailyUsable2 = userConfig.batteryCapacity * BATTERY_USABLE_FRAC
    const dailyStored2 = Math.min(dailyUsable2, dailySurplus2 * BATTERY_ROUND_TRIP)
    const socPct = isBatActive
      ? Math.round((dailyStored2 / (dailyUsable2 || 1)) * 100)
      : 0

    return {
      month: `${month} ${index < 12 ? '25' : '26'}`,
      batterySocAvg: socPct,
      ...metrics,
    }
  })
}
